import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import {
  getConsent,
  setConsent,
  defaultAllowed,
  defaultDenied,
  type ConsentCategories,
} from "@/lib/consent";

export const CookieConsent = () => {
  const [bannerOpen, setBannerOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [prefs, setPrefs] = useState<ConsentCategories>(defaultDenied);

  useEffect(() => {
    const stored = getConsent();
    if (!stored) {
      setBannerOpen(true);
    } else {
      setPrefs({
        necessary: true,
        analytics: stored.analytics,
        functional: stored.functional,
      });
    }

    const openHandler = (e: Event) => {
      const detail = (e as CustomEvent<{ ccpa?: boolean }>).detail;
      if (detail?.ccpa) {
        setPrefs((p) => ({ ...p, analytics: false }));
      }
      setSettingsOpen(true);
    };
    window.addEventListener("opencookiesettings", openHandler);
    return () => window.removeEventListener("opencookiesettings", openHandler);
  }, []);

  const acceptAll = () => {
    setConsent(defaultAllowed);
    setPrefs(defaultAllowed);
    setBannerOpen(false);
    setSettingsOpen(false);
  };

  const rejectAll = () => {
    setConsent(defaultDenied);
    setPrefs(defaultDenied);
    setBannerOpen(false);
    setSettingsOpen(false);
  };

  const savePrefs = () => {
    setConsent(prefs);
    setBannerOpen(false);
    setSettingsOpen(false);
  };

  return (
    <>
      {bannerOpen && (
        <div
          role="region"
          aria-label="Cookie consent"
          className="fixed bottom-0 left-0 right-0 z-[60] border-t border-border bg-card shadow-2xl"
        >
          <div className="container mx-auto max-w-7xl px-4 py-5 flex flex-col md:flex-row md:items-center gap-4">
            <div className="flex-1">
              <h2 className="font-display text-2xl font-bold text-foreground mb-1">
                We value your privacy
              </h2>
              <p className="text-xl text-muted-foreground leading-snug">
                We use cookies to run essential features, measure traffic, and
                power the accessibility widget. You can accept all, reject
                non-essential, or choose what to allow. See our{" "}
                <Link to="/privacy" className="underline text-foreground">
                  Privacy Policy
                </Link>
                .
              </p>
            </div>
            <div className="flex flex-wrap gap-2 md:flex-nowrap">
              <Button
                variant="outline"
                onClick={() => setSettingsOpen(true)}
                className="text-xl"
              >
                Cookie settings
              </Button>
              <Button
                variant="outline"
                onClick={rejectAll}
                className="text-xl"
              >
                Reject all
              </Button>
              <Button onClick={acceptAll} className="text-xl">
                Accept all
              </Button>
            </div>
          </div>
        </div>
      )}

      <Dialog open={settingsOpen} onOpenChange={setSettingsOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">
              Cookie preferences
            </DialogTitle>
            <DialogDescription className="text-xl">
              Choose which categories of cookies and scripts you allow. You can
              change this any time from the footer.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-5 py-2">
            <div className="flex items-start justify-between gap-4 rounded-lg border border-border p-4">
              <div className="flex-1">
                <Label className="text-xl">Strictly necessary</Label>
                <p className="text-xl text-muted-foreground mt-1">
                  Required for authentication, security, and your session.
                  Always active.
                </p>
              </div>
              <Switch checked disabled aria-label="Strictly necessary (always on)" />
            </div>

            <div className="flex items-start justify-between gap-4 rounded-lg border border-border p-4">
              <div className="flex-1">
                <Label htmlFor="cc-analytics" className="text-xl">
                  Analytics
                </Label>
                <p className="text-xl text-muted-foreground mt-1">
                  Google Analytics — anonymous traffic measurement so we can
                  improve the site.
                </p>
              </div>
              <Switch
                id="cc-analytics"
                checked={prefs.analytics}
                onCheckedChange={(v) =>
                  setPrefs((p) => ({ ...p, analytics: v }))
                }
              />
            </div>

            <div className="flex items-start justify-between gap-4 rounded-lg border border-border p-4">
              <div className="flex-1">
                <Label htmlFor="cc-functional" className="text-xl">
                  Functional
                </Label>
                <p className="text-xl text-muted-foreground mt-1">
                  Accessibility widget that lets visitors adjust contrast, font
                  size, and other accessibility options.
                </p>
              </div>
              <Switch
                id="cc-functional"
                checked={prefs.functional}
                onCheckedChange={(v) =>
                  setPrefs((p) => ({ ...p, functional: v }))
                }
              />
            </div>
          </div>

          <DialogFooter className="flex flex-col-reverse sm:flex-row sm:justify-between gap-2">
            <Button variant="outline" onClick={rejectAll} className="text-xl">
              Reject all
            </Button>
            <div className="flex gap-2">
              <Button variant="outline" onClick={savePrefs} className="text-xl">
                Save preferences
              </Button>
              <Button onClick={acceptAll} className="text-xl">
                Accept all
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default CookieConsent;
